import { Injectable } from '@angular/core';
import { BehaviorSubject, first, of } from 'rxjs';
import { concatMap, switchMap, tap } from 'rxjs/operators';

import { CoreModule } from '../../core.module';

import { ServerConnectionService } from '../server-connection.service';
import { StripeService } from '../stripe.service';
import { MemberRoleService } from './member-roles.service';
import { UserService } from './user.service';

import { IMemberRole, IUser } from '../../../types/datatypes';
import { IStripeCustomer } from '../../../types/local';
import { MemberRolesAPIResponse } from '../../../types/responses';
import { OrderService } from './order.service';
import { SettingsService } from './settings.service';
import { PosthogService } from '../posthog.service';

interface customWindow extends Window {
	Rewardful?: any;
	dataLayer?: any;
	development?: any;
}
declare const window: customWindow;
@Injectable({
	providedIn: CoreModule,
})
export class MembershipUpgradeService {
	public author: IUser;

	public customer: IStripeCustomer;

	public plans: IMemberRole[] = [];

	public selectedPlanName = null;

	public plan: IMemberRole = null;

	public goldMemberDiscount;

	public platinumMemberDiscount;

	private _loading = new BehaviorSubject<boolean>(true);

	constructor(
		private memberRoleService: MemberRoleService,
		private serverConnectionService: ServerConnectionService,
		private stripeService: StripeService,
		private userService: UserService,
		private orderService: OrderService,
		private settingsService: SettingsService,
		private posthogService: PosthogService,
	) {
		this._loading.next(true);

		of(null).pipe(concatMap(() => this.userService.author$))
			.pipe(concatMap((author: IUser) => {
				this.author = author;
				this.getCustomer();
				return this.getPlans();
			})).subscribe(async (data: MemberRolesAPIResponse) => {
				if (data && data.success) {
					this.plans = data.roles;
				}
				this._loading.next(false);
			});
		this.initSettings();
	}

	get loading$() {
		return this._loading.asObservable();
	}

	async initSettings() {
		const [gold, platinum] = await Promise.all([
			this.settingsService.getMemberDiscountSettings('gold'),
			this.settingsService.getMemberDiscountSettings('platinum'),
		]);

		this.goldMemberDiscount = gold;
		this.platinumMemberDiscount = platinum;
	}

	getPlans() {
		return of(null).pipe(
			switchMap(() => {
				if (this.plans.length > 0) {
					return of(null);
				}
				return this.memberRoleService.fetchMemberRoles().pipe(first());
			}),
		);
	}

	getCustomer() {
		this.customer = this.createNewCustomer();

		if (!this.author.stripeCustomerId) {
			return;
		}

		this.stripeService.getCustomer(this.author.stripeCustomerId).pipe(first())
			.subscribe(({ customer }: { customer: { id: string; invoice_settings: { default_payment_method: string } } }) => {
				if (customer?.id) {
					this.customer.customerId = customer.id;
					this.customer.paymentMethodId = customer.invoice_settings
						? customer.invoice_settings.default_payment_method
						: undefined;
				}
			});
	}

	select(type: string) {
		const dictionary = { basic: 'gold', premium: 'platinum' };

		// Normalize if it uses the legacy naming. i.e basic, premium.
		const _type = dictionary[type] ? dictionary[type] : type;

		this.selectedPlanName = _type;
		this.plan = this._getPlanByName(_type);

		this.posthogService.capture(this.posthogService.PRODUCT_SELECTED, {
			product_type: 'subscription',
			product_id: this.plan._id,
			product_name: _type,
			price: this.plan.price,
		});
	}

	_getPlanByName(name: string) {
		const { plans } = this;
		if (!plans || !plans.length) return null;

		return plans.find(({ roleName }) => roleName.toLowerCase() === name.toLowerCase());
	}

	createNewCustomer(): IStripeCustomer {
		const _c = {
			customerId: '',
			email: this.author.username,
			name: `${this.author.firstName} ${this.author.lastName}`,
			paymentMethodId: '',
			referral: window.Rewardful?.referral,
		};

		if (_c.referral === '') delete _c.referral;

		return _c;
	}

	upgrade(paymentMethodId) {
		return this.stripeService.upgradeSubscription(this.customer, this.plan, paymentMethodId);
	}

	create(paymentMethodId: string) {
		this.customer.paymentMethodId = paymentMethodId;
		return this.stripeService.createSubscription(this.customer, this.plan, !this.author.stripeCustomerId)
			.pipe(tap((result: any) => {
				if (result.success) {
					this.posthogService.capture(this.posthogService.ORDER_COMPLETED, {
						revenue: this.plan.price,
						payment_method: 'stripe',
						products: [
							{
								product_id: this.plan._id,
								product_name: this.plan.roleName,
								product_type: 'subscription',
								price: this.plan.price,
							},
						],
					});
				}
			}));
	}

	createCartSubscription(planId: string, userInfo) {
		if (!this.userService?.me?._id) {
			return this.userService.createTempUser(userInfo)
				.pipe(concatMap((tempUser) => this.serverConnectionService.post$('/api/subscriptions/create-cart', {
					planId,
					userId: null,
					tempUser: tempUser.user,
				})))
				.pipe(concatMap((result: any) => this.orderService.addItem(result.subscription._id, 'subscription', result.subscription)));
		}

		const body = {
			planId,
			userId: this.userService.me._id,
		};
		return this.serverConnectionService.post$('/api/subscriptions/create-cart', body)
			.pipe(concatMap((result: any) => this.orderService.addItem(result.subscription._id, 'subscription', result.subscription)));
	}

	public getPlanDiscountPercent(plan: IMemberRole): number {
		const percentage = (plan.priceDiscounted / plan.price) * 100;
		return Math.round(100 - percentage);
	}
}
